import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";

const PaymentConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState("processing"); // processing, success, failed

  // Get plan info from URL params (from Stripe redirect) or state
  const urlParams = new URLSearchParams(location.search);
  const success = urlParams.get('success');
  const planId = urlParams.get('plan');
  const billingCycle = urlParams.get('billing');
  
  const { plan, billingCycle: stateBillingCycle, isUpgrade } = location.state || {};
  
  // Use URL params if available, otherwise use state
  const finalPlanId = planId || plan?.id;
  const finalBillingCycle = billingCycle || stateBillingCycle;

  // Debug logging
  console.log('PaymentConfirmation Debug:', {
    urlParams: { success, planId, billingCycle },
    state: { plan, stateBillingCycle, isUpgrade },
    final: { finalPlanId, finalBillingCycle }
  });

  useEffect(() => {
    // Check if this is a success redirect from Stripe
    if (success === 'true') {
      setIsLoading(false);
      setPaymentStatus("success");
    } else {
      // Simulate payment processing for demo
      const timer = setTimeout(() => {
        setIsLoading(false);
        setPaymentStatus("success");
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleContinue = () => {
    if (isUpgrade) {
      // If this was an upgrade, go back to the page they came from
      navigate(-1);
    } else {
      // If this was a new signup, go to dashboard
      navigate("/dashboard");
    }
  };

  // Create plan object from URL params or state
  const createPlanData = (planId) => {
    const planMap = {
      qa_chatbot: {
        id: 'qa_chatbot',
        name: 'Q&A Chatbot Access',
        audioHours: 0
      },
      beginner: {
        id: 'beginner',
        name: 'Beginner',
        audioHours: 8
      },
      standard: {
        id: 'standard',
        name: 'Standard',
        audioHours: 16
      },
      pro: {
        id: 'pro',
        name: 'Pro',
        audioHours: 32
      }
    };
    
    return planMap[planId] || {
      id: planId || 'unknown',
      name: 'Unknown Plan',
      audioHours: 0
    };
  };

  const planData = plan || createPlanData(finalPlanId);

  return (
    <div className="min-h-screen w-full relative">
      {/* Background Image */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('/imgs/auth-bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}>
      </div>

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          {/* Logo */}
          <img src="/imgs/logo-white.png" alt="TableTop" className="mx-auto mb-6" />
          
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                Processing Payment...
              </h1>
              <p className="text-gray-600 mb-6">
                Please wait while we process your {planData?.name || 'subscription'}.
              </p>
            </>
          ) : paymentStatus === "success" ? (
            <>
              <div className="text-green-500 text-6xl mb-4">✓</div>
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                Payment Successful!
              </h1>
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-gray-800 mb-2">Subscription Details:</h3>
                <p className="text-sm text-gray-600">
                  <strong>Plan:</strong> {planData?.name || 'Unknown Plan'}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Billing:</strong> {finalBillingCycle === "annual" ? "Annual" : "Monthly"}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Audio Hours:</strong> {planData?.audioHours || 0} hours/month
                </p>
              </div>
              <p className="text-gray-600 mb-6">
                {isUpgrade 
                  ? "Your account has been upgraded successfully! You can now enjoy your new plan features." 
                  : "Welcome to TableTop Recorder! Your account is now active."
                }
              </p>
              <button
                onClick={handleContinue}
                className="bg-primary hover:bg-primary-light text-white px-6 py-2 rounded-lg font-semibold w-full"
              >
                {isUpgrade ? "Return to Dashboard" : "Go to Dashboard"}
              </button>
            </>
          ) : (
            <>
              <div className="text-red-500 text-6xl mb-4">✗</div>
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                Payment Failed
              </h1>
              <p className="text-gray-600 mb-6">
                There was an issue processing your payment. Please try again.
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => navigate("/plan-select", { state: { isUpgrade } })}
                  className="bg-primary hover:bg-primary-light text-white px-6 py-2 rounded-lg font-semibold w-full"
                >
                  Try Again
                </button>
                <Link
                  to="/dashboard"
                  className="block bg-gray-500 hover:bg-gray-400 text-white px-6 py-2 rounded-lg font-semibold"
                >
                  Go to Dashboard
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation;
